import type { ImportQueueItem } from '@lib/import-queue-ui/types/import-queue-item';
import type { ImportQueueStatus } from '@lib/import-queue-ui/types/import-queue-status';
import { INSIGHT_PIPELINE_STAGE_LABELS } from '@lib/insights/constants/insight-pipeline-stage';
import { MAX_PIPELINE_ATTEMPTS } from '@lib/insights/constants/pipeline-retry';
import type { InsightDocumentStatus } from '@lib/insights/types/insight-node';
import type { InsightQueueRow } from '@lib/insights/types/insight-queue-row';
import { minutesUntilPipelineRedrive } from '@lib/insights/utils/pipeline-retry-schedule';
import { differenceInMinutes, isValid } from 'date-fns';

import type { InsightsBulkImportQueueItem } from '../types/insights-bulk-import-queue-item';
import { insightsQueueItemToImportQueueItem } from './insights-queue-item-to-import-queue-item';

function buildDetail(row: InsightQueueRow, now: Date): string | undefined {
  if (row.status === 'processing' || row.status === 'pending') {
    return row.stage ? INSIGHT_PIPELINE_STAGE_LABELS[row.stage] : undefined;
  }
  if (row.status !== 'failed') return undefined;

  const wait = minutesUntilPipelineRedrive(row, now);
  if (wait === null) return undefined;
  const when = wait === 0 ? 'retrying shortly' : `retrying in ${wait} min`;
  return `Attempt ${row.attemptCount} of ${MAX_PIPELINE_ATTEMPTS} — ${when}`;
}

/**
 * Pending and processing both read as `processing` in the
 * rail — a queued document is work in flight as far as the
 * uploader is concerned.
 */
const QUEUE_STATUS_BY_DOCUMENT_STATUS: Record<
  InsightDocumentStatus,
  ImportQueueStatus
> = {
  pending: 'processing',
  processing: 'processing',
  processed: 'done',
  failed: 'failed',
};

function serverRowToImportQueueItem(
  row: InsightQueueRow,
  now: Date
): ImportQueueItem {
  const status = QUEUE_STATUS_BY_DOCUMENT_STATUS[row.status];

  return {
    clientId: row.documentId,
    fileName: row.fileName,
    relativePath: row.fileName,
    status,
    errorMessage: row.errorMessage ?? undefined,
    navigationId: status === 'done' ? row.documentId : undefined,
    uploadedAt: row.uploadedAt,
    detail: buildDetail(row, now),
    retryDisabledReason:
      status === 'failed' && !row.isRetryable
        ? 'Re-upload required'
        : undefined,
  };
}

/**
 * How long after an upload commits the rail still expects a
 * server row to appear. Each upload batch refetches the
 * queue, so in practice one arrives within seconds; this is
 * slack for a slow request, not a real waiting period.
 *
 * Far below the endpoint's 15-minute processed window, so a
 * finished document can never re-enter the window and read as
 * in-flight again.
 */
const AWAITING_SERVER_ROW_MINUTES = 2;

function isAwaitingServerRow(
  item: InsightsBulkImportQueueItem,
  now: Date
): boolean {
  // No stamp means the item predates the field, so treat it
  // as settled — showing a stale Complete is recoverable on
  // the next poll, whereas a row stuck in `processing` is not.
  if (!item.uploadedAt) return false;
  const uploadedAt = new Date(item.uploadedAt);
  if (!isValid(uploadedAt)) return false;
  return differenceInMinutes(now, uploadedAt) < AWAITING_SERVER_ROW_MINUTES;
}

/**
 * The client queue owns files still in flight; the server
 * owns everything with a row. Where both know about a
 * document the server wins, which is what lets the rail
 * keep showing work after a refresh drops client state.
 */
export function mergeInsightQueueItems(
  clientItems: InsightsBulkImportQueueItem[],
  serverRows: InsightQueueRow[],
  now: Date
): ImportQueueItem[] {
  const serverIds = new Set(serverRows.map(row => row.documentId));

  const clientOnly = clientItems
    .filter(item => !item.documentId || !serverIds.has(item.documentId))
    .map(item => ({
      ...insightsQueueItemToImportQueueItem(item),
      uploadedAt: item.uploadedAt,
      // The client's `done` means the upload committed, not
      // that the document finished processing — the pipeline
      // starts after that, so rendering it as Complete labels
      // in-flight work finished.
      //
      // Only while the server could still be catching up.
      // A missing server row is ambiguous: either it has not
      // appeared yet, or the document finished and dropped out
      // of the processed window, and the client queue keeps
      // items for the whole session. Reading the second case
      // as in-flight would leave a finished import spinning
      // forever with polling already stopped.
      ...(item.status === 'done' && isAwaitingServerRow(item, now)
        ? { status: 'processing' as const }
        : {}),
    }));

  const fromServer = serverRows.map(row =>
    serverRowToImportQueueItem(row, now)
  );

  return [...clientOnly, ...fromServer];
}
